import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Copy, Loader2, Plus, ShieldAlert, Sparkles, Trash2, Upload, Video } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Nav } from "@/components/Nav";
import { LockedAI, MeetingStatus, SectionLabel, StatusBadge } from "@/components/Status";
import { useAuth } from "@/context/AuthContext";
import { LANGS, LOCALE, useLang } from "@/i18n";
import { api, errCode } from "@/lib/api";

const fmtDate = (iso, lang) => (iso ? new Date(iso).toLocaleString(LOCALE[lang], { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

function NewRoomDialog({ open, onOpenChange, onCreated }) {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ title: "", description: "", language: lang, scheduled_at: "", lobby_enabled: true });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, scheduled_at: form.scheduled_at ? new Date(form.scheduled_at).toISOString() : null };
      const { data } = await api.post("/meetings", payload);
      onCreated(data);
      onOpenChange(false);
      setForm({ title: "", description: "", language: lang, scheduled_at: "", lobby_enabled: true });
    } catch (err) {
      toast.error(t(`errors.${errCode(err)}`, errCode(err)));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-vox-line bg-vox-panel sm:max-w-md" data-testid="new-room-dialog">
        <DialogHeader><DialogTitle className="text-vox-text">{t("dashboard.newRoom")}</DialogTitle><DialogDescription className="sr-only">{t("dashboard.roomTitle")}</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-2">
          <div>
            <label className="mono-label mb-2 block">{t("dashboard.roomTitle")}</label>
            <input required className="input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="new-room-title-input" />
          </div>
          <div>
            <label className="mono-label mb-2 block">{t("dashboard.roomLang")}</label>
            <div className="flex gap-1 rounded-full border border-vox-line bg-vox-deep/60 p-1">
              {LANGS.map((l) => (
                <button key={l} type="button" onClick={() => setForm({ ...form, language: l })} data-testid={`new-room-lang-${l}`}
                  className={`flex-1 rounded-full py-1.5 font-mono text-xs uppercase transition-colors ${form.language === l ? "bg-vox-card text-vox-text" : "text-vox-dim hover:text-vox-text"}`}>{l}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="mono-label mb-2 block">{t("dashboard.roomWhen")}</label>
            <input type="datetime-local" className="input" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} data-testid="new-room-when-input" />
          </div>
          <div>
            <label className="mono-label mb-2 block">{t("dashboard.roomDesc")}</label>
            <textarea rows={2} className="input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="new-room-desc-input" />
          </div>
          <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-vox-line bg-vox-deep/40 p-3" data-testid="new-room-lobby-toggle">
            <input type="checkbox" className="mt-0.5 h-4 w-4 accent-[#94BFC7]" checked={form.lobby_enabled} onChange={(e) => setForm({ ...form, lobby_enabled: e.target.checked })} />
            <span>
              <span className="block text-sm text-vox-text">{t("dashboard.lobby")}</span>
              <span className="block text-xs text-vox-dim">{t("dashboard.lobbyHint")}</span>
            </span>
          </label>
          <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="new-room-submit-button">
            {busy ? <Loader2 size={16} className="animate-spin" /> : t("common.create")}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function UploadCard({ onCreated }) {
  const { t, lang } = useLang();
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);
  const submit = async (e) => {
    e.preventDefault();
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("audio", file);
      fd.append("title", title || file.name);
      fd.append("language", lang);
      const { data } = await api.post("/ai/upload", fd);
      onCreated(data);
      setFile(null);
      setTitle("");
      toast.success(t("room.recSaved"));
    } catch (err) {
      toast.error(t(`errors.${errCode(err)}`, errCode(err)));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="panel p-6" data-testid="upload-recording-card">
      <div className="mb-4 flex items-center gap-2 text-vox-text"><Upload size={16} className="text-vox-accent" /><span className="font-medium">{t("dashboard.upload")}</span></div>
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); setFile(e.dataTransfer.files?.[0] || null); }}
        onClick={() => inputRef.current?.click()}
        className={`cursor-pointer rounded-xl border border-dashed px-4 py-8 text-center text-sm transition-colors ${drag ? "border-vox-bright bg-vox-card" : "border-vox-line text-vox-dim hover:border-vox-accent"}`}
        data-testid="recording-upload-dropzone"
      >
        {file ? <span className="text-vox-text">{file.name} · {(file.size / 1048576).toFixed(1)} MB</span> : t("dashboard.uploadHint")}
        <input ref={inputRef} type="file" accept="audio/*,video/*,.mp3,.mp4,.wav,.m4a,.webm,.ogg" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="upload-file-input" />
      </div>
      <input className="input mt-4" placeholder={t("dashboard.uploadTitle")} value={title} onChange={(e) => setTitle(e.target.value)} data-testid="upload-title-input" />
      <button type="submit" disabled={!file || busy} className="btn-primary mt-4 w-full" data-testid="upload-recording-trigger-button">
        {busy ? <><Loader2 size={16} className="animate-spin" />{t("dashboard.uploading")}</> : <><Sparkles size={16} />{t("dashboard.uploadBtn")}</>}
      </button>
    </form>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [m, j] = await Promise.all([api.get("/meetings"), user.ai_enabled ? api.get("/ai/jobs") : Promise.resolve({ data: [] })]);
      setMeetings(m.data);
      setJobs(j.data);
    } catch (e) {
      toast.error(t(`errors.${errCode(e)}`, errCode(e)));
    }
  }, [user.ai_enabled, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!jobs.some((j) => !["done", "error"].includes(j.status))) return undefined;
    const id = setInterval(() => api.get("/ai/jobs").then((r) => setJobs(r.data)).catch(() => {}), 4000);
    return () => clearInterval(id);
  }, [jobs]);

  const copyGuest = (m) => {
    navigator.clipboard.writeText(`${window.location.origin}/join/${m.id}?code=${m.join_code}`);
    toast.success(t("common.copied"));
  };
  const remove = async (m) => {
    if (!window.confirm(t("common.confirmDelete"))) return;
    await api.delete(`/meetings/${m.id}`);
    setMeetings((prev) => prev.filter((x) => x.id !== m.id));
  };

  return (
    <div className="min-h-screen" data-testid="dashboard-page">
      <Nav />
      <main className="mx-auto max-w-6xl px-6 py-12">
        <div className="reveal mb-12 flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="mono-label mb-3">{t("dashboard.hello")}, {user.name}</div>
            <h1 className="text-4xl font-semibold tracking-tight text-vox-text sm:text-5xl">{t("dashboard.title")}</h1>
            <div className="mt-3 flex items-center gap-3 text-sm text-vox-muted">
              <span data-testid="dashboard-org-name">{user.org_name}</span>
              <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 font-mono text-[11px] uppercase tracking-wider ${user.ai_enabled ? "border-emerald-500/40 text-emerald-300" : "border-vox-amber/40 text-vox-amber"}`} data-testid="dashboard-ai-status">
                <Sparkles size={11} /> {user.ai_enabled ? t("dashboard.aiOn") : t("dashboard.aiOff")}
              </span>
            </div>
          </div>
          <button type="button" onClick={() => setOpen(true)} className="btn-primary" data-testid="new-room-create-button"><Plus size={16} /> {t("dashboard.newRoom")}</button>
        </div>
        {user.ai_consent_required && (
          <Link to="/org" className="reveal mb-10 flex items-start gap-3 rounded-2xl border border-vox-amber/40 bg-vox-amber/5 p-5 text-sm text-vox-muted transition-colors duration-200 hover:border-vox-amber/70" data-testid="dashboard-ai-consent-banner">
            <ShieldAlert size={18} className="mt-0.5 shrink-0 text-vox-amber" />
            <span><span className="font-medium text-vox-text">{t("org.aiConsentTitle")}</span> — {t("dashboard.aiConsentBanner")}</span>
          </Link>
        )}

        <div className="grid gap-10 lg:grid-cols-[1fr_360px]">
          <section className="reveal reveal-1">
            <SectionLabel index="01">{t("nav.dashboard")}</SectionLabel>
            {meetings === null ? (
              <div className="flex items-center gap-2 text-sm text-vox-dim"><Loader2 size={14} className="animate-spin" />{t("common.loading")}</div>
            ) : meetings.length === 0 ? (
              <div className="panel p-8" data-testid="meetings-empty">
                <p className="mb-6 text-sm text-vox-muted">{t("dashboard.empty")}</p>
                <ol className="space-y-4">
                  {[1, 2, 3].map((n) => (
                    <li key={n} className="flex gap-4">
                      <span className="font-mono text-xs text-vox-accent pt-0.5">0{n}</span>
                      <div>
                        <div className="text-sm font-medium text-vox-text">{t(`dashboard.onb${n}_t`)}</div>
                        <div className="mt-0.5 text-sm text-vox-dim">{t(`dashboard.onb${n}_d`)}</div>
                      </div>
                    </li>
                  ))}
                </ol>
                <button type="button" onClick={() => setOpen(true)} className="btn-primary mt-8" data-testid="empty-create-room-button"><Plus size={16} /> {t("dashboard.newRoom")}</button>
              </div>
            ) : (
              <div className="space-y-3" data-testid="meeting-list-container">
                {meetings.map((m) => (
                  <article key={m.id} className="panel group flex flex-wrap items-center justify-between gap-4 p-5 transition-colors duration-200 hover:border-vox-accent/60" data-testid="meeting-card-item">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-3">
                        <Link to={`/meetings/${m.id}`} className="truncate text-base font-medium text-vox-text hover:text-vox-bright" data-testid="meeting-card-title">{m.title}</Link>
                        <MeetingStatus status={m.status} />
                      </div>
                      <div className="mt-1.5 flex flex-wrap items-center gap-3 font-mono text-[11px] uppercase tracking-wider text-vox-dim">
                        <span>{m.language}</span>
                        <span>{fmtDate(m.scheduled_at || m.created_at, lang)}</span>
                        {m.jobs_count > 0 && <span className="text-vox-accent">{m.jobs_count} {t("dashboard.protocols")}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={() => copyGuest(m)} className="btn-ghost !px-3 !py-2" title={t("dashboard.guestLink")} data-testid="copy-guest-link-button"><Copy size={14} /></button>
                      <Link to={`/meetings/${m.id}`} className="btn-ghost !px-4 !py-2" data-testid="meeting-details-link">{t("dashboard.details")}</Link>
                      {m.status !== "ended" && (
                        <button type="button" onClick={() => navigate(`/room/${m.id}`)} className="btn-primary !px-4 !py-2" data-testid="meeting-join-button"><Video size={14} /> {t("dashboard.join")}</button>
                      )}
                      {(user.role !== "member" || m.owner_id === user.id) && (
                        <button type="button" onClick={() => remove(m)} className="rounded-full p-2 text-vox-dim transition-colors hover:bg-vox-rec/10 hover:text-[#ff9b9e]" data-testid="meeting-delete-button"><Trash2 size={14} /></button>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>

          <aside className="reveal reveal-2 space-y-6">
            <SectionLabel index="02">{t("dashboard.ai")}</SectionLabel>
            {user.ai_enabled ? (
              <>
                <UploadCard onCreated={(job) => setJobs((prev) => [job, ...prev])} />
                <div className="panel p-6" data-testid="recent-jobs">
                  <div className="mono-label mb-4">{t("dashboard.recentJobs")}</div>
                  {jobs.length === 0 ? <p className="text-sm text-vox-dim">—</p> : (
                    <ul className="space-y-3">
                      {jobs.slice(0, 6).map((j) => (
                        <li key={j.id} className="flex items-center justify-between gap-3" data-testid="recent-job-item">
                          <Link to={j.meeting_id ? `/meetings/${j.meeting_id}?job=${j.id}` : `/jobs/${j.id}`} className="truncate text-sm text-vox-text hover:text-vox-bright">{j.title}</Link>
                          <StatusBadge status={j.status} />
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </>
            ) : <LockedAI />}
          </aside>
        </div>
      </main>
      <NewRoomDialog open={open} onOpenChange={setOpen} onCreated={(m) => { setMeetings((prev) => [m, ...(prev || [])]); if (!m.scheduled_at) navigate(`/room/${m.id}`); }} />
    </div>
  );
}
